import type { HitProps } from '@components/algolia/Hit';

type Device = 'desktop' | 'mobile';

/**
 * Hotspots are flattened to this many optional, numbered slots per tab per
 * device rather than a nested Prismic Group, because the legacy custom-type
 * builder doesn't support nesting a Group inside slice `items` (already
 * group-shaped). See `imageHotspot.schema.md`.
 */
const MAX_HOTSPOTS_PER_TAB = 6;
const HOTSPOT_SLOT_NUMBERS = Array.from(
  { length: MAX_HOTSPOTS_PER_TAB },
  (_, i) => i + 1
);

/**
 * The subset of an `image_hotspot` slice item this module relies on. The fetch
 * layer types the Prismic response as `any`, so the shape is declared here —
 * the same minimal-declaration approach as
 * {@link ../transforms/plpCrossLinking.PlpCrossLinkingDocument}, but note
 * `image_hotspot` is an embedded *slice* on `category_page`/`landing_page`
 * (Prismic's `{ primary, items }` slice shape, read straight off
 * `categoryPage.body`/`landingPage.body1`) — not a standalone document fetched
 * by its own query, so there's no `data` wrapper here.
 * Hotspot slot fields (`hotspot_{n}_sku`, `hotspot_{n}_{device}_position`) are
 * read dynamically via the index signature rather than named individually —
 * see `imageHotspot.schema.md` for the full field list.
 */
export interface ImageHotspotItem {
  tab_label?: string | null;
  desktop_image?: { url?: string | null; alt?: string | null } | null;
  mobile_image?: { url?: string | null; alt?: string | null } | null;
  heading?: string | null;
  quote?: string | null;
  quote_attribution_line_1?: string | null;
  quote_attribution_line_2?: string | null;
  quote_eyebrow_label?: string | null;
  quote_trigger_label?: string | null;
  dynamic_peek?: boolean | null;
  co_op_brand?: string | null;
  [dynamicField: string]: unknown;
}

export interface ImageHotspotSlice {
  id?: string;
  slice_type?: string;
  primary?: {
    co_op_brand_overall?: string | null;
    heading?: string | null;
  } | null;
  items?: ImageHotspotItem[] | null;
}

export interface ImageHotspotImage {
  url: string;
  alt: string | null;
}

/** A resolved, in-stock hotspot. OOS or unresolved SKUs never reach this shape. */
export interface ImageHotspotDot {
  x: number;
  y: number;
  product: HitProps;
  /** Optional colourway SKU (a variant's own SKU, not the parent product
      code in `product`) — lets a slot pin a specific colourway rather than
      whatever Algolia treats as the product's default. `null` when unset;
      resolving it to a colour key is `ProductTile`'s job (mirrors
      `PlpCrossLinkingCard`'s `colourKeyForVariantSku`), since a stale/unknown
      SKU should degrade to the default colourway rather than drop the hotspot. */
  variantSku: string | null;
}

export interface ImageHotspotCta {
  label: string;
  url: string;
}

export interface ImageHotspotTab {
  /** Stable per-render id (`tab-{index}`) — slice items carry no Prismic UID of their own. */
  id: string;
  label: string;
  desktopImage: ImageHotspotImage;
  mobileImage: ImageHotspotImage;
  desktopHotspots: ImageHotspotDot[];
  mobileHotspots: ImageHotspotDot[];
  heading: string | null;
  quote: string | null;
  quoteAttributionLine1: string | null;
  quoteAttributionLine2: string | null;
  /** Overrides the `imageHotspot.staffPicksEyebrow` i18n default (ED-345 QA
      #11) — `null` when the merchandiser left it blank, so `ReviewPanel`
      falls back to the translated default rather than rendering nothing. */
  quoteEyebrowLabel: string | null;
  /** Overrides the `imageHotspot.staffAdviceLabel` i18n default (ED-345 QA
      #12) — same null-means-unset fallback as `quoteEyebrowLabel`. */
  quoteTriggerLabel: string | null;
  dynamicPeek: boolean;
  ctas: ImageHotspotCta[];
  /** The tab's own `co_op_brand`, falling back to the slice-level
      `co_op_brand_overall` when the tab didn't set its own — resolved here so
      every consumer (GA4 tracking, ED-416/ED-447) gets one final value
      instead of re-deriving the fallback itself. */
  coOpBrand: string | null;
}

export interface ImageHotspotData {
  coOpBrandOverall: string | null;
  /** Optional heading rendered above the whole component (digi merch request, ED-345 comment) — distinct from each tab's own `heading`. */
  heading: string | null;
  tabs: ImageHotspotTab[];
}

const trimmed = (value: unknown): string | null =>
  typeof value === 'string' ? value.trim() || null : null;

const toImage = (
  image: { url?: string | null; alt?: string | null } | null | undefined
): ImageHotspotImage | null => {
  const url = trimmed(image?.url);
  return url ? { url, alt: trimmed(image?.alt) } : null;
};

/** Shared across both devices — one product per hotspot slot, placed
    independently per breakpoint via its own `position` field. */
const hotspotSkuField = (n: number): string => `hotspot_${n}_sku`;
const hotspotPositionField = (n: number, device: Device): string =>
  `hotspot_${n}_${device}_position`;
/** Optional colourway pin — see `ImageHotspotDot.variantSku`. */
const hotspotVariantSkuField = (n: number): string =>
  `hotspot_${n}_variant_sku`;

/**
 * Parses a `position` field's `"x/y"` string (the format `HotspotDebugOverlay`
 * copies to the clipboard) into numeric percentages. Returns `null` for
 * anything that isn't cleanly two in-range percentages — blank, missing the
 * `/`, a blank half, non-numeric, or outside 0–100 — so a malformed value is
 * treated the same as an unfilled slot rather than rendering a hotspot at a
 * wrong/NaN/off-image position.
 */
const parsePosition = (raw: unknown): { x: number; y: number } | null => {
  const value = trimmed(raw);
  if (!value) {
    return null;
  }

  const parts = value.split('/');
  if (parts.length !== 2) {
    return null;
  }

  const [rawX, rawY] = parts.map((part) => part.trim());
  if (!rawX || !rawY) {
    return null;
  }

  const x = Number(rawX);
  const y = Number(rawY);

  const inRange = (n: number): boolean => n >= 0 && n <= 100;

  return Number.isFinite(x) && Number.isFinite(y) && inRange(x) && inRange(y)
    ? { x, y }
    : null;
};

/**
 * Author-ordered SKUs across every hotspot slot, every tab. Used by
 * `getSliceProducts` (CIP/CLP/homepage `getStaticProps`) to build one
 * batched `getProductsByProductCodes` lookup before the transform runs.
 */
export const collectImageHotspotSkus = (
  slice: ImageHotspotSlice | null
): string[] => {
  const items = slice?.items ?? [];

  return items.flatMap((item) =>
    HOTSPOT_SLOT_NUMBERS.flatMap((n) => {
      const sku = trimmed(item?.[hotspotSkuField(n)]);
      return sku ? [sku] : [];
    })
  );
};

export const collectImageHotspotVariantSkus = (
  slice: ImageHotspotSlice | null
): string[] =>
  (slice?.items ?? []).flatMap((item) =>
    HOTSPOT_SLOT_NUMBERS.flatMap((n) => {
      const sku = trimmed(item?.[hotspotVariantSkuField(n)]);
      return sku ? [sku] : [];
    })
  );

/**
 * Resolves a tab's hotspot slots for one device. A slot is skipped when its
 * (device-shared) SKU is blank, that device's `position` doesn't parse to two
 * numbers, the SKU didn't resolve to a product, or the resolved product is
 * out of stock — hiding the hotspot entirely rather than rendering a dead or
 * broken dot (SPEC.md decision 8). A slot with a SKU but only one device's
 * position filled in still resolves for that device alone — the same product
 * simply doesn't get a hotspot on the other breakpoint's image.
 */
const collectHotspots = (
  item: ImageHotspotItem,
  device: Device,
  resolvedProducts: Record<string, HitProps>
): ImageHotspotDot[] =>
  HOTSPOT_SLOT_NUMBERS.flatMap((n) => {
    const sku = trimmed(item[hotspotSkuField(n)]);
    const position = parsePosition(item[hotspotPositionField(n, device)]);

    if (!sku || !position) {
      return [];
    }

    const product = resolvedProducts[sku];

    if (!product || product.availableOnPlp === false) {
      return [];
    }

    return [
      {
        x: position.x,
        y: position.y,
        product,
        variantSku: trimmed(item[hotspotVariantSkuField(n)]),
      },
    ];
  });

const collectCtas = (item: ImageHotspotItem): ImageHotspotCta[] =>
  ([1, 2] as const).flatMap((n) => {
    const ctaLabel = trimmed(item[`cta_${n}_label`]);
    const ctaUrl = trimmed(item[`cta_${n}_link`]);
    return ctaLabel && ctaUrl ? [{ label: ctaLabel, url: ctaUrl }] : [];
  });

/**
 * Maps an `image_hotspot` slice to render-ready props. `resolvedProducts` is
 * the SKU→record map from `getProductsByProductCodes`, resolved once in
 * `getStaticProps` and threaded down through the slice zone/handler as a plain
 * object (not the `Map` `getProductsByProductCodes` returns — a `Map` can't
 * cross the `getStaticProps` → page-props JSON boundary, see Task 3). This
 * function itself stays pure.
 *
 * A tab with no label or no image on either breakpoint is dropped rather than
 * rendered broken. Returns `null` when no tab is usable, so the page never
 * renders an empty component.
 */
export const transformImageHotspot = (
  slice: ImageHotspotSlice | null,
  resolvedProducts: Record<string, HitProps> = {}
): ImageHotspotData | null => {
  if (!slice) {
    return null;
  }

  const coOpBrandOverall = trimmed(slice.primary?.co_op_brand_overall);

  const tabs: ImageHotspotTab[] = (slice.items ?? []).flatMap((item, index) => {
    const label = trimmed(item?.tab_label);
    const desktopImage = toImage(item?.desktop_image);
    const mobileImage = toImage(item?.mobile_image);

    if (!label || !desktopImage || !mobileImage) {
      return [];
    }

    return [
      {
        id: `tab-${index}`,
        label,
        desktopImage,
        mobileImage,
        desktopHotspots: collectHotspots(item, 'desktop', resolvedProducts),
        mobileHotspots: collectHotspots(item, 'mobile', resolvedProducts),
        heading: trimmed(item?.heading),
        quote: trimmed(item?.quote),
        quoteAttributionLine1: trimmed(item?.quote_attribution_line_1),
        quoteAttributionLine2: trimmed(item?.quote_attribution_line_2),
        quoteEyebrowLabel: trimmed(item?.quote_eyebrow_label),
        quoteTriggerLabel: trimmed(item?.quote_trigger_label),
        dynamicPeek: item?.dynamic_peek === true,
        ctas: collectCtas(item),
        coOpBrand: trimmed(item?.co_op_brand) ?? coOpBrandOverall,
      },
    ];
  });

  if (tabs.length === 0) {
    return null;
  }

  return {
    coOpBrandOverall,
    heading: trimmed(slice.primary?.heading),
    tabs,
  };
};
